/**
 * Email Addresses tab for the Settings page.
 *
 * Displays an explanatory introduction, a "Create" button, and a
 * table of the user's email addresses with Edit, Delete, and
 * Send Verification actions.
 *
 * @module components/EmailAddressesTab
 */

import React, { useState,
                useEffect,
                useCallback }   from 'react';
import { Pencil,
         Trash2,
         SendHorizonal,
         RefreshCw }            from 'lucide-react';
import Modal                    from './Modal';
import { useI18n }              from '../dataProviders/I18nProvider';
import apiClient                from '../procedural/apiClient';
import * as constants           from '../constants';

/**
 * Format a date string or Date object for display.
 *
 * @param {string|Date} value - ISO date string or Date.
 * @returns {string} Formatted date string.
 */
function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleDateString('en-US', {
    year:  'numeric',
    month: 'long',
    day:   'numeric',
  });
}

/**
 * Renders the Email Addresses settings tab.
 *
 * @returns {React.ReactElement}
 */
export default function EmailAddressesTab() {
  const { t } = useI18n();

  const [emails, setEmails]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [editTarget, setEditTarget]           = useState(null);

  /* ---- Load emails ------------------------------------------------------ */

  const loadEmails = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/emails');
      setEmails(res.data.emails);
    } catch {
      setError(t('settings.emails.error.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadEmails(); }, [loadEmails]);

  /* ---- Send verification ------------------------------------------------ */

  /**
   * @param {object} email - Email record.
   */
  async function handleSendVerification(email) {
    setError(null);
    setSuccessMsg(null);
    if (email.verified) {
      setSuccessMsg(t('settings.emails.verifyAlreadyVerified'));
      return;
    }
    try {
      await apiClient.post(`/api/emails/${email.id}/resend-verification`);
      setSuccessMsg(t('settings.emails.verifySent'));
    } catch {
      setError(t('settings.emails.error.verify'));
    }
  }

  /* ---- Create callback -------------------------------------------------- */

  /**
   * @param {object} newEmail - Newly created email record from server.
   */
  function handleCreated(newEmail) {
    setShowCreateModal(false);
    setEmails((prev) => [...prev, newEmail]);
    setSuccessMsg(t('settings.emails.verifySent'));
  }

  /* ---- Delete callback -------------------------------------------------- */

  function handleDeleted(emailId) {
    setDeleteTarget(null);
    setEmails((prev) => prev.filter((e) => e.id !== emailId));
  }

  /* ---- Edit callback ---------------------------------------------------- */

  /**
   * @param {object} updated - Updated email record from server.
   */
  function handleUpdated(updated) {
    setEditTarget(null);
    if (updated.isPrimary) {
      setEmails((prev) => prev.map((e) =>
        e.id === updated.id ? updated : { ...e, isPrimary: false },
      ));
    } else {
      setEmails((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    }
  }

  /* ---- Render ----------------------------------------------------------- */

  return (
    <div className="settings-emails">
      <div className="settings-emails-intro">
        <p>{t('settings.emails.intro')}</p>
        <ul>
          <li>{t('settings.emails.purpose.verification')}</li>
          <li>{t('settings.emails.purpose.loginCode')}</li>
          <li>{t('settings.emails.purpose.passwordReset')}</li>
          <li>{t('settings.emails.purpose.notifications')}</li>
        </ul>
        <p>{t('settings.emails.delivery')}</p>
      </div>

      <div className="settings-emails-button-row">
        <button
          type="button"
          className="settings-emails-create-btn"
          onClick={() => { setShowCreateModal(true); setError(null); setSuccessMsg(null); }}
        >
          {t('settings.emails.createButton')}
        </button>
        <button
          type="button"
          className="settings-emails-refresh-btn"
          title={t('settings.emails.refresh')}
          onClick={() => { setError(null); setSuccessMsg(null); loadEmails(); }}
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {(error || successMsg) && (
        <div className={successMsg ? 'auth-success' : 'auth-error'} style={{ marginTop: '12px' }}>
          {successMsg || error}
        </div>
      )}

      {loading && <p style={{ padding: '16px 0' }}>Loading…</p>}

      {!loading && emails.length === 0 && (
        <p className="settings-emails-empty">{t('settings.emails.noEmails')}</p>
      )}

      {!loading && emails.length > 0 && (
        <table className="settings-emails-table">
          <thead>
            <tr>
              <th>{t('settings.emails.col.address')}</th>
              <th>{t('settings.emails.col.createdAt')}</th>
              <th>{t('settings.emails.col.updatedAt')}</th>
              <th>{t('settings.emails.col.verified')}</th>
              <th>{t('settings.emails.col.primary')}</th>
              <th>{t('settings.emails.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {emails.map((email) => (
              <tr key={email.id}>
                <td>{email.address}</td>
                <td>{formatDate(email.createdAt)}</td>
                <td>{formatDate(email.updatedAt)}</td>
                <td>{email.verified ? t('settings.emails.yes') : t('settings.emails.no')}</td>
                <td>{email.isPrimary ? t('settings.emails.yes') : t('settings.emails.no')}</td>
                <td className="settings-emails-actions">
                  <button
                    type="button"
                    className="settings-emails-action-btn"
                    title={t('settings.emails.action.verify')}
                    onClick={() => handleSendVerification(email)}
                  >
                    <SendHorizonal size={16} />
                  </button>
                  <button
                    type="button"
                    className="settings-emails-action-btn"
                    title={t('settings.emails.action.edit')}
                    onClick={() => { setEditTarget(email); setError(null); setSuccessMsg(null); }}
                  >
                    <Pencil size={16} />
                  </button>
                  {emails.length > 1 && (
                    <button
                      type="button"
                      className="settings-emails-action-btn settings-emails-action-btn--danger"
                      title={t('settings.emails.action.delete')}
                      onClick={() => { setDeleteTarget(email); setError(null); setSuccessMsg(null); }}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateEmailModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
      {deleteTarget && (
        <DeleteEmailModal
          email={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
        />
      )}
      {editTarget && (
        <EditEmailModal
          email={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/*  Create Email Modal                                                     */
/* ----------------------------------------------------------------------- */

/**
 * Modal for creating a new email address.
 *
 * @param {object}   props
 * @param {Function} props.onClose   - Dismiss callback.
 * @param {Function} props.onCreated - Called with the new email record on success.
 * @returns {React.ReactElement}
 */
function CreateEmailModal({ onClose, onCreated }) {
  const { t } = useI18n();

  const [address, setAddress]       = useState('');
  const [error, setError]           = useState(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * @param {React.FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!address) return;

    setSubmitting(true);
    try {
      const res = await apiClient.post('/api/emails', { address });
      onCreated(res.data.email);
    } catch (err) {
      if (err.response?.data?.error === 'conflict') {
        setError(t('settings.emails.error.createConflict'));
      } else {
        setError(t('settings.emails.error.create'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.emails.createModal.title')} onClose={onClose}>
      <form className="auth-form" onSubmit={handleSubmit}>
        {error && <div className="auth-error">{error}</div>}

        <div className="auth-field">
          <label className="auth-label" htmlFor="create-email-address">
            {t('settings.emails.createModal.label')}
          </label>
          <input
            id="create-email-address"
            className="auth-input"
            type="email"
            autoComplete="email"
            maxLength={constants.MAX_EMAIL_LENGTH}
            placeholder={t('settings.emails.createModal.placeholder')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
            {t('settings.emails.createModal.cancel')}
          </button>
          <button type="submit" className="modal-btn modal-btn--primary" disabled={submitting || !address}>
            {t('settings.emails.createModal.ok')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------------------------------------------------------- */
/*  Delete Email Modal                                                     */
/* ----------------------------------------------------------------------- */

/**
 * Confirmation modal for deleting an email address.
 *
 * @param {object}   props
 * @param {object}   props.email    - The email record to delete.
 * @param {Function} props.onClose  - Dismiss callback.
 * @param {Function} props.onDeleted - Called with the email ID on success.
 * @returns {React.ReactElement}
 */
function DeleteEmailModal({ email, onClose, onDeleted }) {
  const { t } = useI18n();

  const [error, setError]           = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete() {
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.delete(`/api/emails/${email.id}`);
      onDeleted(email.id);
    } catch {
      setError(t('settings.emails.error.delete'));
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.emails.deleteModal.title')} onClose={onClose}>
      <p style={{ color: 'var(--color-danger)', marginBottom: '12px', lineHeight: 1.6 }}>
        {t('settings.emails.deleteModal.warning')}
      </p>
      <p style={{ fontWeight: 600, marginBottom: '4px' }}>
        {t('settings.emails.deleteModal.address').replace('{address}', email.address)}
      </p>

      {error && <div className="auth-error" style={{ marginTop: '12px' }}>{error}</div>}

      <div className="modal-actions">
        <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
          {t('settings.emails.deleteModal.cancel')}
        </button>
        <button type="button" className="modal-btn modal-btn--danger" onClick={handleDelete} disabled={submitting}>
          {t('settings.emails.deleteModal.delete')}
        </button>
      </div>
    </Modal>
  );
}

/* ----------------------------------------------------------------------- */
/*  Edit Email Modal                                                       */
/* ----------------------------------------------------------------------- */

/**
 * Modal for editing an email address and its primary flag.
 * Includes Revert, Cancel, and OK buttons with dirty-state logic.
 * Warns if changing the address of a verified email will reset verification.
 *
 * @param {object}   props
 * @param {object}   props.email     - The email record to edit.
 * @param {Function} props.onClose   - Dismiss callback.
 * @param {Function} props.onUpdated - Called with the updated email record on success.
 * @returns {React.ReactElement}
 */
function EditEmailModal({ email, onClose, onUpdated }) {
  const { t } = useI18n();

  const [address, setAddress]       = useState(email.address);
  const [isPrimary, setIsPrimary]   = useState(email.isPrimary);
  const [error, setError]           = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const addressChanged  = address !== email.address;
  const primaryChanged  = isPrimary !== email.isPrimary;
  const isDirty         = addressChanged || primaryChanged;
  const willLoseVerified = email.verified && addressChanged;

  function handleRevert() {
    setAddress(email.address);
    setIsPrimary(email.isPrimary);
    setError(null);
  }

  /**
   * @param {React.FormEvent} e
   */
  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!isDirty) return;

    const payload = {};
    if (addressChanged) payload.address   = address;
    if (primaryChanged) payload.isPrimary = isPrimary;

    setSubmitting(true);
    try {
      const res = await apiClient.put(`/api/emails/${email.id}`, payload);
      onUpdated(res.data.email);
    } catch (err) {
      if (err.response?.data?.error === 'conflict') {
        setError(t('settings.emails.error.updateConflict'));
      } else {
        setError(t('settings.emails.error.update'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t('settings.emails.editModal.title')} onClose={onClose}>
      <form className="auth-form" onSubmit={handleSubmit}>
        {error && <div className="auth-error">{error}</div>}

        <div className="auth-field">
          <label className="auth-label" htmlFor="edit-email-address">
            {t('settings.emails.editModal.address.label')}
          </label>
          <input
            id="edit-email-address"
            className="auth-input"
            type="email"
            autoComplete="email"
            maxLength={constants.MAX_EMAIL_LENGTH}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={submitting}
          />
        </div>

        {willLoseVerified && (
          <div className="auth-error" style={{ fontSize: '13px' }}>
            {t('settings.emails.editModal.verifiedWarning')}
          </div>
        )}

        <div className="auth-field">
          <label className="settings-emails-checkbox-label">
            <input
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              disabled={submitting || email.isPrimary}
            />
            {t('settings.emails.editModal.isPrimary.label')}
          </label>
          {email.isPrimary && (
            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              {t('settings.emails.editModal.primaryLocked')}
            </span>
          )}
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="modal-btn modal-btn--cancel"
            onClick={handleRevert}
            disabled={!isDirty || submitting}
          >
            {t('settings.emails.editModal.revert')}
          </button>
          <button type="button" className="modal-btn modal-btn--cancel" onClick={onClose} disabled={submitting}>
            {t('settings.emails.editModal.cancel')}
          </button>
          <button type="submit" className="modal-btn modal-btn--primary" disabled={!isDirty || submitting || !address}>
            {t('settings.emails.editModal.ok')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
